import React, { useEffect, useState } from "react";
import { withStyles } from "@material-ui/core/styles";
import withMobileDialog from "@material-ui/core/withMobileDialog";
import * as Yup from "yup";
import AppBar from "@material-ui/core/AppBar";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import CircularProgress from "@material-ui/core/CircularProgress";
import { Link } from "react-router-dom";
import EmailOutlinedIcon from "@material-ui/icons/EmailOutlined";
import CloudDownloadOutlinedIcon from "@material-ui/icons/CloudDownloadOutlined";
import {useDispatch, useSelector} from "react-redux";
import portfolioAction from "../../actions/portfolio.action";
import { isPremiumView } from "../../utilities/access";
import {CyManagerTypes} from "../../utilities/managerTypes";

const EmailField = ({ onChange = () => null, ...props }) => {
  const [email, setEmail] = useState("");
  const isValid = Yup.string().email().isValidSync(email);
  useEffect(() => {
    onChange(email, isValid);
  }, [email]);
  return (
    <TextField
      autoFocus
      type="email"
      label="Email Address"
      value={email}
      onChange={(evt) => setEmail(evt.target.value)}
      error={!isValid}
      helperText={!isValid && "Please Enter a Valid Email Address"}
      {...props}
      data-cy="summary.emailPDFDialog.email"
    />
  );
};

const SubmitButton = (props) => (
  <Button variant='contained' color="primary" {...props} data-cy="summary.emailPDFDialog.submit">
    Email Summary PDF
  </Button>
);

const CancelButton = (props) => (
  <Button color="primary" {...props}>
    Cancel
  </Button>
);

export const Acknowledgement = ({ onChange = () => null, ...props }) => {
  const [acknowledged, setAcknowledged] = useState(false);
  useEffect(() => {
    onChange(acknowledged, true);
  }, [acknowledged]);
  return (
    <FormControlLabel
      control={
        <Checkbox
          required
          color="primary"
          checked={acknowledged}
          onChange={(evt) => setAcknowledged(evt.target.checked)}
          {...props}
          data-cy="summary.emailPDFDialog.acknowledgement"
        />
      }
      label="Acknowledged that the content is subject to the posted disclosures, as well as the terms, conditions, and limitations of the customer agreement."
    />
  );
};

const Resume = ({portfolioId, householdId, doSessionId}) => {
  const onboarding = useSelector(state => state.quik);
  const dispatch = useDispatch();
  const resumeOnboard = () => {
    dispatch(portfolioAction.getQuikFilledForms(portfolioId));
  };
  useEffect(() => {
    if (onboarding.onboardStyle !== 'do') {
      if (!onboarding.pending && onboarding.id) {
        const quikId = onboarding.id;
        // const html = onboarding.html;
        window.localStorage.setItem('quikform', JSON.stringify(onboarding));
        const tab = window.open(`/Portal/onboard?quikId=${encodeURIComponent(quikId)}`, '_blank');
        if (!tab) {
          console.log('popup blocked')
          // window.location.href = `/Portal/onboard?quikId=${encodeURIComponent(quikId)}`;
        }
        dispatch(portfolioAction.resetQuikFormData());
      }
    }
  }, [onboarding.id]);
  return <div className="left">
    <Typography>Please complete onboarding documents.</Typography>
    {
      !!doSessionId ?
        <Link to={`/Portal/household/${householdId}/onboard/${doSessionId}?portfolioId=${portfolioId}`}>
          <Button variant="outlined" color='primary'>Continue</Button>
        </Link> :
        <Button variant="outlined" color='primary' disabled={onboarding.pending} onClick={resumeOnboard}>
          {
            onboarding.pending ? <CircularProgress size={20}/> : 'Continue'
          }
        </Button>
    }
  </div>
};

const EmailDialog = withMobileDialog()(
  withStyles((theme) => ({
    dialogContent: {
      "& > :first-child": { marginBottom: 16 },
    },
  }))(({ onSubmit, onClose, isOpen, classes }) => {
    const [validEmail, setValidEmail] = useState("");
    return (
      <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Email Summary PDF</DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <EmailField
            style={{minWidth: '70%'}}
            onChange={(email, isValid) => isValid && setValidEmail(email)}
          />
        </DialogContent>
        <DialogActions>
          <CancelButton onClick={onClose} />
          <SubmitButton
            disabled={!validEmail}
            onClick={() => onSubmit(validEmail)}
          />
        </DialogActions>
      </Dialog>
    );
  })
);

// TODO: convert these anonymous function components to named components
// XXX: complicated styles are required for compatibility with
//      left-side navigation panel
const OnboardBar = withStyles((theme) => ({
  appBar: {
    "@media print": { display: "none" },
    position: "fixed",
    top: "auto",
    bottom: 0,
    boxShadow: `0 0 10px rgba(0,0,0,0.6)`,
    left: `${
      theme.wbiComponent.staticNavigation.totalWidth +
      2 * theme.wbiComponent.cyContainer.basePadding
    }px`,
    [`@media only screen and ${theme.breakpoints.downNoMedia("md")}`]: {
      left: "139px",
    },
    [theme.breakpoints.down("lg")]: {
      left: "150px",
    },
    right: 0,
    width: "auto",
    "& p": { display: "inline" },
  },
  toolbar: {
    display: "flex",
    flexFlow: "row nowrap",
    justifyContent: "space-between",
    "& > div > *": { margin: "0.5em" },
  },
  downloadOptions: {
    position: "absolute",
    bottom: -100,
    right: 30,
    opacity: 0,
    transition: "all 500ms ease",
    "&.visible": {
      bottom: 70,
      opacity: 1,
    },
    "& .MuiButtonBase-root": {
      background: theme.palette.common.white,
      display: "block",
      marginBottom: 12,
    },
  },
}))(
  ({ portfolioId,
    hideOnboardOption,
    brokerDealer,
    hasValidRepCode,
    onOnboard,
    onEmail,
    onDownload,
    isDownloading,
    classes,
    onStrategyChange,
    showStrategyChangeButton,
    isStrategyChange,
    portfolio,
    custodians,
    appName,
    modelDisclosureEnabled,
    doSessionId,
    accessLevel,
    householdId
  }) => {
    const [showEmailDialog, setShowEmailDialog] = useState(false);
    const [downloadOptionVisible, setDownloadOptionVisible] = useState(false);
    const portfolioState = useSelector(state => state.portfolio);
    const submitEmail = (email) => {
      setShowEmailDialog(false);
      onEmail(email);
    };

    const submitDownload = (includeModelConstituentDisclosure) => (e) => {
      e.preventDefault();
      setDownloadOptionVisible(false);
      onDownload(includeModelConstituentDisclosure);
    };

    // for different onboarding states
    const Onboard = () => (
      <div className="left">
        {isStrategyChange ? (
          <Typography>Are you ready to onboard the changes?</Typography>
        ) : (
          <Typography>Are you ready to sign up now?</Typography>
        )}
        <Button
          variant="outlined"
          color="primary"
          disabled={!onOnboard || CyManagerTypes.disabledOnboarding(portfolio.metadata)}
          onClick={onOnboard}
        >
          Onboard
        </Button>
      </div>
    );
    const WaitForThirdParty = () => (
      <div className="left">
        <Typography>Please complete onboarding via the normal {appName} process.</Typography>
      </div>
    );
    const Wait = () => (
      <div className="left">
        <Typography>Onboarding documents have been submitted.</Typography>
        {
          !!doSessionId &&
            <Link to={`/Portal/household/${householdId}/onboard/${doSessionId}?portfolioId=${portfolioId}`}>
              <Button variant="outlined" color='primary'>View Onboarding</Button>
            </Link>
        }
      </div>
    );
    const Done = () => (
      <div className="left">
        <Typography>Portfolio has been onboarded. </Typography>
        {showStrategyChangeButton && (
          <Button variant="outlined" color="primary" onClick={onStrategyChange} disabled={portfolioState.requesting}>
            Change Strategy {portfolioState.requesting && <CircularProgress size={20} />}
          </Button>
        )}
      </div>
    );

    // const InvalidRepCodeAlert = () => (
    //   <div className="left">
    //     <Typography>
    //       Please enter a valid PAS Rep Code for onboarding.{" "}
    //     </Typography>
    //     <Link to={"/Portal/user/repCodes"}>
    //       <Button variant="outlined" color="primary">
    //         Go To My Profile
    //       </Button>
    //     </Link>
    //   </div>
    // );

    // const InvalidBrokerDealer = ({ brokerDealerName }) => (
    //   <div className="left">
    //     <Typography>
    //       You Broker Dealer <b>{brokerDealerName}</b> does not have a
    //       solicitor's agreement signed. Please contact support.
    //     </Typography>
    //   </div>
    // );

    // determine which to show on the left
    const Left = ({
      hideOnboardOption,
      brokerDealer,
      hasValidRepCode,
      onboardStatus = "in-review",
      custodian
    }) => {
      if (hideOnboardOption) {
        return <div className="left" />;
      }
      // TODO: TURNING OFF BD AND PAS CHECKING FOR NOW 20200111
      // if (brokerDealer && !brokerDealer.verified) {
      //   return <InvalidBrokerDealer brokerDealerName={brokerDealer.name}/>
      // }
      // if (!hasValidRepCode) {
      //   return <InvalidRepCodeAlert />
      // }
      switch (onboardStatus) {
        case "in-review":
          return isPremiumView(accessLevel) ? <Onboard /> : <div />;
        case "forms-generated":
          if (custodian && !custodian.onboardingRules.digitalOnboardingEnabled) {
            return <WaitForThirdParty />;
          }
          return <Resume
            portfolioId={portfolioId}
            doSessionId={doSessionId}
            householdId={householdId}
          />;
        case "forms-submitted":
        case "forms-signed":
          return <Wait doSessionId={doSessionId} />;
        case "onboarded":
          return <Done />;
        default:
          return <Onboard />;
      }
    };

    const EmailCTA = () => (<React.Fragment>
      <div
        className={`${classes.downloadOptions} ${
          downloadOptionVisible ? "visible" : ""
        }`}
      >
        <Grid
          container
          direction="column"
          justifyContent="center"
          alignItems="flex-end"
        >
          <Button
            variant="outlined"
            color="primary"
            disableRipple
            onClick={submitDownload(0)}
          >
            {portfolio.onboardStatus === 'in-review' ? 'Draft' : ''} Summary PDF
          </Button>
          {
            modelDisclosureEnabled &&
            <Button
              variant="outlined"
              color="primary"
              disableRipple
              onClick={submitDownload(1)}
            >
              Model Constituent Disclosure PDF
            </Button>
          }
        </Grid>
      </div>
      <div className="right">
        <Typography>Other Options:</Typography>
        <Button
          variant="outlined"
          color="primary"
          disabled={!onEmail}
          onClick={() => setShowEmailDialog(true)}
          data-cy="summary.actions.sendEmail"
        >
          <EmailOutlinedIcon fontSize={"small"} />
          &nbsp;&nbsp;Email
        </Button>
        <EmailDialog
          isOpen={showEmailDialog}
          onSubmit={submitEmail}
          onClose={() => setShowEmailDialog(false)}
        />
        <Button
          variant="outlined"
          color="primary"
          disabled={isDownloading}
          onClick={() => setDownloadOptionVisible(!downloadOptionVisible)}
        >
          <CloudDownloadOutlinedIcon fontSize={"small"} />
          &nbsp;&nbsp;Download&nbsp;&nbsp;
          {isDownloading && <CircularProgress size={20} />}
        </Button>
      </div>
    </React.Fragment>);

    return (
      <AppBar color="inherit" className={classes.appBar}>
        <Toolbar className={classes.toolbar}>
          <Left
            hideOnboardOption={hideOnboardOption}
            brokerDealer={brokerDealer}
            hasValidRepCode={hasValidRepCode}
            onboardStatus={portfolio.onboardStatus}
            custodian={custodians.find(c => c.id === portfolio.CustodianId)}
          />
          <EmailCTA />
        </Toolbar>
      </AppBar>
    );
  }
);

export default OnboardBar;



// WEBPACK FOOTER //
// ./src/components/UI/OnboardBar.jsx